import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';

/**
 * Default predicate for `linkControlEnabledTo`: treat `null`, `undefined`,
 * `''`, `false`, and empty arrays as "no value". Anything else enables the
 * dependent control. Exported so callers can compose it (e.g. wrap with extra
 * checks) instead of re-implementing the empties list.
 */
export const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '' || value === false) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/**
 * Toggle a `dependent` control's enabled state based on a `source` control's
 * value: enable when source has a meaningful value, disable otherwise.
 *
 * Why a helper instead of `[disabled]="!source.value?.length"` in the
 * template: when a control is driven by `formControlName` / `formControl`,
 * the DOM `[disabled]` binding does NOT reliably propagate to
 * `FormControl.disabled` — Angular even logs a warning. The single source
 * of truth must be the FormControl itself; this util keeps that invariant
 * with `enable()`/`disable()` calls. `emitEvent: false` prevents the toggle
 * from re-triggering downstream `valueChanges` listeners.
 *
 * `takeUntilDestroyed` requires an injection context, so the caller passes
 * its `DestroyRef` — works in any component / directive without needing a
 * dedicated `Subject<void>` teardown.
 *
 * @example
 *   linkControlEnabledTo(form.get('docks')!, form.get('storages')!, this.destroyRef);
 *
 * @example  with a custom predicate (enable only when source value > 5)
 *   linkControlEnabledTo(
 *     form.get('details')!,
 *     form.get('count')!,
 *     this.destroyRef,
 *     (n): boolean => typeof n === 'number' && n > 5,
 *   );
 */
export const linkControlEnabledTo = (
  dependent: AbstractControl,
  source: AbstractControl,
  destroyRef: DestroyRef,
  hasValue: (value: unknown) => boolean = hasMeaningfulValue,
): void => {
  const apply = (value: unknown): void => {
    if (hasValue(value)) {
      dependent.enable({ emitEvent: false });
    } else {
      dependent.disable({ emitEvent: false });
    }
  };
  apply(source.value);
  source.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(apply);
};
